import { ApiResponse, Permission, Profile, UserScopes } from "src/models";
import { LicenseStatus } from "src/models/hierarchy";
import { LONG_REQUEST_TIMEOUT_MS, apiSlice } from "../../apiSlice";

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
}

export interface ForgotPasswordPayload {
  email: string;
  code?: string;
  password?: string;
}

export interface PermissionsData {
  roles: string[];
  permissions: Permission[];
  scopes: UserScopes;
  mfaRequired: boolean;
  /** The caller's district license (R19); null for platform admins. */
  license?: { status: LicenseStatus } | null;
}

export const authApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<LoginResponse, { email: string; password: string }>(
      {
        query: (credentials) => ({
          url: "/api/account/login",
          method: "POST",
          body: credentials,
        }),
        transformResponse: (response: ApiResponse<LoginResponse>) =>
          response.data,
      },
    ),
    /**
     * Development sign-in (`POST /api/account/dev-login`): a token for an existing staff profile
     * with no password. The route exists only when the account service runs with DEV_AUTH_SECRET,
     * which it refuses in production; the console offers it only in development builds.
     */
    devLogin: builder.mutation<LoginResponse, { email: string }>({
      query: (body) => ({
        url: "/api/account/dev-login",
        method: "POST",
        body,
      }),
      transformResponse: (response: ApiResponse<LoginResponse>) =>
        response.data,
    }),
    resetPassword: builder.mutation<ApiResponse, ForgotPasswordPayload>({
      query: (credentials) => ({
        url: "/api/account/forgot-password",
        method: "PUT",
        body: credentials,
      }),
    }),
    getUser: builder.query<{ profile: Profile }, void>({
      query: () => ({ url: "/api/account/me", method: "GET" }),
      transformResponse: (response: ApiResponse<{ profile: Profile }>) =>
        response.data,
      providesTags: ["Profile"],
    }),
    getPermissions: builder.query<PermissionsData, void>({
      query: () => ({ url: "/api/account/me/permissions", method: "GET" }),
      transformResponse: (response: ApiResponse<PermissionsData>) =>
        response.data,
    }),
    updateProfile: builder.mutation<
      ApiResponse,
      Partial<
        Pick<
          Profile,
          "firstName" | "lastName" | "displayName" | "timezone" | "phoneNumber"
        >
      >
    >({
      query: (profile) => ({
        url: "/api/account/me",
        method: "PUT",
        body: profile,
      }),
      invalidatesTags: ["Profile"],
    }),
    updateProfileImage: builder.mutation<ApiResponse, FormData>({
      query: (body) => ({
        url: "/api/account/me/profile-image",
        method: "PUT",
        body,
        timeout: LONG_REQUEST_TIMEOUT_MS,
      }),
      invalidatesTags: ["Profile"],
    }),
    updatePassword: builder.mutation<
      ApiResponse,
      { previousPassword: string; proposedPassword: string }
    >({
      query: (payload) => ({
        url: "/api/account/me/password",
        method: "PUT",
        body: payload,
      }),
    }),
    discoverSignIn: builder.query<
      {
        mode: "sso" | "password" | "unknown";
        identityProvider?: string;
        hostedUiDomain?: string;
        clientId?: string;
        districtName?: string;
      },
      string
    >({
      query: (email) => ({
        url: `/api/account/sso/discover?email=${encodeURIComponent(email)}`,
        method: "GET",
      }),
      transformResponse: (
        response: ApiResponse<{
          mode: "sso" | "password" | "unknown";
          identityProvider?: string;
          hostedUiDomain?: string;
          clientId?: string;
          districtName?: string;
        }>,
      ) => response.data,
    }),
    markMfaEnrolled: builder.mutation<ApiResponse, void>({
      query: () => ({ url: "/api/account/me/mfa/enrolled", method: "PUT" }),
      invalidatesTags: ["Profile"],
    }),
    logoutServer: builder.mutation<
      ApiResponse,
      { refreshToken: string | null }
    >({
      query: (body) => ({ url: "/api/account/logout", method: "POST", body }),
    }),
  }),
});

export const {
  useDevLoginMutation,
  useLoginMutation,
  useGetUserQuery,
  useLazyGetUserQuery,
  useResetPasswordMutation,
  useLazyGetPermissionsQuery,
  useUpdateProfileMutation,
  useUpdateProfileImageMutation,
  useUpdatePasswordMutation,
  useLogoutServerMutation,
  useLazyDiscoverSignInQuery,
  useMarkMfaEnrolledMutation,
} = authApiSlice;
